/**
 * ActionsCatalogPage — custom Blueprints actions catalog (T14).
 *
 * Replaces the T12 placeholder with a full actions catalog built on the
 * public `ScaffolderApi.listActions()` method.
 *
 * ## Why this is custom-built
 *
 * `ActionsPage` from `@backstage/plugin-scaffolder` is NOT exported from the
 * plugin's public TypeScript API. Two upstream PRs (#28846, #30039) attempting
 * to expose it were closed unmerged. Backstage maintainers recommend building
 * custom pages from the public `ScaffolderApi` — see PLAN.md section
 * "Architecture: building scaffolder sub-pages from public APIs".
 *
 * ## Layout (modelled on screenshots/celodrome-scaffolder-actions.png)
 *
 * - Persistent search box (substring match on id + description).
 * - Actions grouped by namespace prefix (part before first `:` in `id`),
 *   alphabetised. Each namespace is a collapsible Accordion section.
 *   Sections auto-expand when a search query is active and they have matches.
 * - Per-action card: monospace `id` chip + description + collapsible
 *   Input / Output schema tables + optional examples (CodeSnippet YAML).
 * - Graceful fallback: if schema.input/output lacks a `properties` map,
 *   renders the raw JSON via CodeSnippet rather than crashing.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CodeSnippet } from '@backstage/core-components';
import {
  Accordion,
  AccordionPanel,
  AccordionTrigger,
  Alert,
  Badge,
  Box,
  Cell,
  CellText,
  Container,
  Flex,
  FullPage,
  SearchField,
  Skeleton,
  Table,
  Text,
  type ColumnConfig,
} from '@backstage/ui';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import type {
  Action,
  ScaffolderUsageExample,
} from '@backstage/plugin-scaffolder-common';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { BlueprintsHeader } from './BlueprintsHeader';

const MONO = { fontFamily: 'var(--bui-font-monospace)' } as const;

/** A monospace token in a neutral outline: action ids, field names, types. */
function CodeTag({
  children,
  size = 'small',
}: {
  children: ReactNode;
  size?: 'small' | 'medium';
}) {
  return (
    <Badge
      size={size}
      style={{ flexShrink: 0, border: '1px solid var(--bui-border-2)' }}
    >
      <Text
        variant={size === 'medium' ? 'body-medium' : 'body-small'}
        style={{
          ...MONO,
          // Trim the line box to the capitals so the text sits in the middle.
          textBox: 'trim-both cap alphabetic',
        }}
      >
        {children}
      </Text>
    </Badge>
  );
}

// ── Type helpers ──────────────────────────────────────────────────────────────

/** Extract the namespace (part before first ':') from an action id. */
export function namespace(id: string): string {
  const colon = id.indexOf(':');
  return colon === -1 ? id : id.slice(0, colon);
}

/** Format a JSONSchema7 type (string, array, object, …) for display. */
export function formatType(schema: JSONSchema7Definition | undefined): string {
  if (!schema || typeof schema === 'boolean') return '—';
  const s = schema as JSONSchema7;
  if (Array.isArray(s.type)) return s.type.join(' | ');
  if (s.type === 'array') {
    const items = s.items;
    if (items && typeof items === 'object' && !Array.isArray(items)) {
      const it = items as JSONSchema7;
      const itemType = Array.isArray(it.type) ? it.type.join('|') : it.type;
      if (itemType) return `array<${itemType}>`;
    }
    return 'array';
  }
  return s.type ?? '—';
}

/**
 * True if the schema looks like a plain flat object schema we can render as
 * a table. Requires a `properties` map; no `properties` triggers the fallback.
 */
export function isFlatObjectSchema(schema: JSONSchema7 | undefined): boolean {
  return !!(
    schema &&
    typeof schema.properties === 'object' &&
    schema.properties !== null
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Renders a JSON Schema object as a flat Name/Title/Description/Type table.
 * Falls back to a raw JSON CodeSnippet if the structure is not a flat object
 * schema (i.e., missing `properties` map).
 */
type SchemaRow = {
  /**
   * Row key. React Aria keeps rows and columns in one key space, so a property
   * called "name", "title", "description" or "type" would replace the column
   * of the same id; prefix it.
   */
  id: string;
  name: string;
  required: boolean;
  schema: JSONSchema7Definition;
};

const SCHEMA_COLUMNS: ColumnConfig<SchemaRow>[] = [
  {
    id: 'name',
    label: 'Name',
    isRowHeader: true,
    cell: row => (
      <Cell>
        <Flex align="center" gap="1">
          <CodeTag>{row.name}</CodeTag>
          {row.required && (
            <Text color="danger" weight="bold" title="Required">
              *
            </Text>
          )}
        </Flex>
      </Cell>
    ),
  },
  {
    id: 'title',
    label: 'Title',
    cell: row => <CellText title={propertyOf(row).title ?? ''} />,
  },
  {
    id: 'description',
    label: 'Description',
    cell: row => (
      <Cell>
        <Text variant="body-small" style={{ whiteSpace: 'normal' }}>
          {propertyOf(row).description ?? ''}
        </Text>
      </Cell>
    ),
  },
  {
    id: 'type',
    label: 'Type',
    cell: row => (
      <Cell>
        <CodeTag>{formatType(row.schema)}</CodeTag>
      </Cell>
    ),
  },
];

function propertyOf(row: SchemaRow): JSONSchema7 {
  return typeof row.schema === 'boolean' ? {} : row.schema;
}

/**
 * Renders a JSON Schema object as a flat Name/Title/Description/Type table.
 * Falls back to a raw JSON CodeSnippet if the structure is not a flat object
 * schema (i.e., missing `properties` map).
 */
function SchemaTable({
  schema,
}: {
  schema: JSONSchema7 | undefined;
}): JSX.Element | null {
  if (!schema) return null;

  // Fallback: unexpected / non-flat schema structure
  if (!isFlatObjectSchema(schema)) {
    return (
      <Box mt="1">
        <CodeSnippet
          language="json"
          text={JSON.stringify(schema, null, 2)}
          showLineNumbers={false}
        />
      </Box>
    );
  }

  const properties = schema.properties as Record<string, JSONSchema7Definition>;
  const required = Array.isArray(schema.required) ? schema.required : [];
  const rows: SchemaRow[] = Object.entries(properties).map(([name, prop]) => ({
    id: `property:${name}`,
    name,
    required: required.includes(name),
    schema: prop,
  }));

  if (rows.length === 0) {
    return <Text color="secondary">No properties defined.</Text>;
  }

  return (
    <Box
      style={{
        border: '1px solid var(--bui-border-1)',
        borderRadius: 'var(--bui-radius-2)',
      }}
    >
      <Table
        columnConfig={SCHEMA_COLUMNS}
        data={rows}
        pagination={{ type: 'none' }}
      />
    </Box>
  );
}

/** One action: id, description, Input table, Output table, examples. */
function ActionCard({ action }: { action: Action }): JSX.Element {
  const inputSchema = action.schema?.input as JSONSchema7 | undefined;
  const outputSchema = action.schema?.output as JSONSchema7 | undefined;
  const examples: ScaffolderUsageExample[] = action.examples ?? [];

  return (
    <Flex
      direction="column"
      gap="3"
      px="5"
      py="4"
      style={{ borderTop: '1px solid var(--bui-border-1)' }}
    >
      {/* Id */}
      <Flex>
        <CodeTag size="medium">{action.id}</CodeTag>
      </Flex>

      {/* Description */}
      {action.description && (
        <Text as="p" color="secondary">
          {action.description}
        </Text>
      )}

      {/* Input schema */}
      {inputSchema && (
        <Flex direction="column" gap="2">
          <Text as="h3" weight="bold">
            Input
          </Text>
          <SchemaTable schema={inputSchema} />
        </Flex>
      )}

      {/* Output schema */}
      {outputSchema && (
        <Flex direction="column" gap="2">
          <Text as="h3" weight="bold">
            Output
          </Text>
          <SchemaTable schema={outputSchema} />
        </Flex>
      )}

      {/* Examples */}
      {examples.length > 0 && (
        <Flex direction="column" gap="2">
          <Text as="h3" weight="bold">
            {examples.length === 1 ? 'Example' : 'Examples'}
          </Text>
          {examples.map((ex, i) => (
            <Flex key={i} direction="column" gap="1">
              {ex.description && (
                <Text variant="body-small" color="secondary">
                  {ex.description}
                </Text>
              )}
              <CodeSnippet
                language="yaml"
                text={ex.example}
                showLineNumbers={false}
              />
              {ex.notes && (
                <Text variant="body-small" color="secondary">
                  {ex.notes}
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

/** Collapsible section for one namespace group, as the original's panel. */
function NamespaceSection({
  ns,
  actions,
  defaultExpanded,
}: {
  ns: string;
  actions: Action[];
  defaultExpanded: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Re-sync when defaultExpanded changes (e.g. search toggled on/off).
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  return (
    <Box mb="3">
      <Accordion
        isExpanded={expanded}
        onExpandedChange={setExpanded}
        style={{ padding: 0 }}
      >
        <AccordionTrigger>
          <Flex align="baseline" gap="2" px="4" py="3" style={{ flex: 1 }}>
            <Text weight="bold">{ns}</Text>
            <Text variant="body-small" color="secondary">
              ({actions.length} {actions.length === 1 ? 'action' : 'actions'})
            </Text>
          </Flex>
        </AccordionTrigger>
        <AccordionPanel>
          {actions.map(action => (
            <ActionCard key={action.id} action={action} />
          ))}
        </AccordionPanel>
      </Accordion>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Actions catalog page for the Blueprints hub.
 *
 * Mounted at `/create/actions` as a direct FlatRoutes sibling in App.tsx.
 * Default export is required — do not rename.
 */
export default function ActionsCatalogPage(): JSX.Element {
  const scaffolderApi = useApi(scaffolderApiRef);

  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [query, setQuery] = useState('');

  // ── Fetch all actions once on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    scaffolderApi
      .listActions()
      .then(result => {
        if (!cancelled) {
          setActions(result);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scaffolderApi]);

  // ── Filter by search query (case-insensitive substring) ──────────────────
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter(
      a =>
        a.id.toLowerCase().includes(q) ||
        (a.description ?? '').toLowerCase().includes(q),
    );
  }, [actions, query]);

  // ── Group by namespace prefix, sort alphabetically ───────────────────────
  const groups = useMemo<{ ns: string; actions: Action[] }[]>(() => {
    const map = new Map<string, Action[]>();
    for (const action of filtered) {
      const ns = namespace(action.id);
      const bucket = map.get(ns) ?? [];
      bucket.push(action);
      map.set(ns, bucket);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ns, acts]) => ({
        ns,
        actions: [...acts].sort((a, b) => a.id.localeCompare(b.id)),
      }));
  }, [filtered]);

  const isSearchActive = query.trim().length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FullPage>
      <BlueprintsHeader
        subtitle="Installed scaffolder actions available in Blueprint templates"
        pageTitleOverride="Blueprints – Actions"
      />
      <Container py="6">
        {loading && (
          <Box role="progressbar" aria-label="Loading actions">
            <Skeleton width="100%" height={4} rounded />
          </Box>
        )}

        {!loading && error && (
          <Alert
            role="alert"
            status="danger"
            icon
            title="Unable to load actions"
            description={error.message}
          />
        )}

        {!loading && !error && (
          <>
            {/* Page intro */}
            <Flex direction="column" gap="2" mb="5">
              <Text as="h2" variant="title-small">
                Installed actions
              </Text>
              <Text as="p" color="secondary">
                All scaffolder actions available for use in Blueprint templates.
                Reference these action ids in your template's{' '}
                <code style={MONO}>steps[].action</code> field.
              </Text>
            </Flex>

            {/* Search toolbar */}
            <Box mb="5" style={{ maxWidth: 640 }}>
              <SearchField
                aria-label="Search actions"
                placeholder="Search for an action…"
                value={query}
                onChange={setQuery}
              />
            </Box>

            {/* Empty state */}
            {groups.length === 0 && (
              <Flex direction="column" align="center" gap="2" py="8">
                <Text as="h2" variant="title-x-small" color="secondary">
                  {isSearchActive
                    ? 'No actions match your search'
                    : 'No actions installed'}
                </Text>
                <Text as="p" color="secondary">
                  {isSearchActive
                    ? `No actions found for "${query}". Try a different search term.`
                    : 'The scaffolder backend has no actions registered. Check the backend configuration.'}
                </Text>
              </Flex>
            )}

            {/* Namespace groups — auto-expand when searching */}
            {groups.map(({ ns, actions: nsActions }) => (
              <NamespaceSection
                key={ns}
                ns={ns}
                actions={nsActions}
                defaultExpanded={isSearchActive}
              />
            ))}
          </>
        )}
      </Container>
    </FullPage>
  );
}
